import { useMemo } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";
import omit from "lodash/omit";
import compact from "lodash/compact";

import { useResponsiveFlag } from "../Utils/ResponsiveQueries";
import Icon from "../Utils/Icon";
import { useFormatMessage } from "../Intl/Context";

const BUTTON_FILTERED_PROPS = [
  "iconPosition",
  "iconClassName",
  "label",
  "screenReaderLabel",
  "labelClassName",
  "hideLabelOnMobile",
  "innerClassName",
  "isActive",
  "className"
];

export const useButtonProps = props => {
  const {
    isActive,
    children,
    label: labelText,
    screenReaderLabel: screenReaderLabelText,
    labelClassName,
    innerClassName,
    iconClassName
  } = props;

  const iconPosition = props.iconPosition === void 0 ? "before" : props.iconPosition;
  const hideLabelOnMobile =
    props.hideLabelOnMobile === void 0 ? false : props.hideLabelOnMobile;

  const t = useFormatMessage();
  const activeLabel = t(intlMessages.active);

  const passthroughProps = omit(props, BUTTON_FILTERED_PROPS);

  const { isDesktop } = useResponsiveFlag();

  const label = useMemo(() => {
    if (!labelText || (!isDesktop && hideLabelOnMobile)) return null;

    const className = classNames("button-label", labelClassName);

    return (
      <span key="label" className={className}>
        {labelText}
      </span>
    );
  }, [hideLabelOnMobile, isDesktop, labelText, labelClassName]);

  const screenReaderLabel = useMemo(() => {
    if (!screenReaderLabelText) return null;

    return (
      <span key="screenReaderLabel" className="sr-only">
        {screenReaderLabelText}
      </span>
    );
  }, [screenReaderLabelText]);

  const icon = useMemo(() => {
    if (!iconClassName) return null;

    const className = classNames(iconClassName, {
      [`button-icon-${iconPosition}`]: iconPosition && label
    });

    return <Icon key="icon" className={className} />;
  }, [iconClassName, iconPosition, label]);

  const srActiveTag = useMemo(
    () =>
      isActive && (
        <span key="sr-tag" className="sr-only">
          {activeLabel}
        </span>
      ),
    [activeLabel, isActive]
  );

  const className = classNames(props.className, {
    active: isActive,
    "without-mobile-label": hideLabelOnMobile
  });

  const content = useMemo(() => {
    const content = [icon, label, screenReaderLabel];
    const className = classNames(["button-inner", innerClassName]);

    if (compact(content).length) {
      return (
        <>
          <span className={className}>{content}</span>
          {srActiveTag}
        </>
      );
    } else {
      return children;
    }
  }, [icon, label, screenReaderLabel, innerClassName, srActiveTag, children]);

  return { passthroughProps, icon, label, srActiveTag, className, content, isActive };
};

const intlMessages = defineMessages({
  active: {
    id: "button.sr.active",
    defaultMessage: "(actif)"
  }
});
